/**
 * Proposals: a sales deal's figures filled into Otii's Word template, then
 * locked as a PDF. The field names are what the template's {{…}} slots use.
 */
import { z } from "zod";

export interface ProposalSettings {
  /** How long a proposal is valid: sets {{ValidUntil}}. */
  validDays: number;
  /** The stage a deal moves to when a proposal is made (only ever forwards). */
  stageId: string | null;
  /** The email's defaults; {first_name}, {organisation}, {reference} and {sender_name} are filled in. */
  emailSubject: string;
  emailMessage: string;
}

export const DEFAULT_PROPOSAL_SETTINGS: ProposalSettings = {
  validDays: 30,
  stageId: "st-proposal",
  emailSubject: "Your Otii proposal {reference}",
  emailMessage:
    "Hi {first_name},\n\nThank you for your time at the demo. Your proposal for {organisation} is attached.\n\nIf you have any questions, just reply to this email.\n\n{sender_name}",
};

/** The template's slots as typed in Word, listed in Admin. */
export const PROPOSAL_FIELDS: { slot: string; example: string }[] = [
  { slot: "{{ClientName}}", example: "Brightstart Nursery Group" },
  { slot: "{{ContactName}}", example: "Tom Hughes" },
  { slot: "{{ContactFirstName}}", example: "Tom" },
  { slot: "{{Date}}", example: "19 October 2026" },
  { slot: "{{ValidUntil}}", example: "18 November 2026" },
  { slot: "{{QuoteReference}}", example: "Q-0001" },
  { slot: "{{DealReference}}", example: "D-0042" },
  { slot: "{{OwnerName}}", example: "Stuart Graham" },
  { slot: "{{OwnerEmail}}", example: "stuart@get-otii.com" },
  { slot: "{{NumNurseries}}", example: "12" },
  { slot: "{{NurseriesText}}", example: "12 nurseries" },
  { slot: "{{MonthlyExcVatAnnual}}", example: "£1,234.00" },
  { slot: "{{MonthlyIncVatAnnual}}", example: "£1,480.80" },
  { slot: "{{InvoiceValueAnnual}}", example: "£14,808.00" },
  { slot: "{{InvoiceValueAnnualIncVat}}", example: "£17,769.60" },
  { slot: "{{TotalAnnualCost}}", example: "£14,808.00" },
  { slot: "{{TotalAnnualCostIncVat}}", example: "£17,769.60" },
  { slot: "{{MonthlyExcVatQuarterly}}", example: "£1,300.00" },
  { slot: "{{MonthlyIncVatQuarterly}}", example: "£1,560.00" },
  { slot: "{{InvoiceValueQuarterly}}", example: "£3,900.00" },
  { slot: "{{InvoiceValueQuarterlyIncVat}}", example: "£4,680.00" },
  { slot: "{{TotalQuarterlyCost}}", example: "£15,600.00" },
  { slot: "{{TotalQuarterlyCostIncVat}}", example: "£18,720.00" },
  { slot: "{{#Discount}} … {{/Discount}}", example: "Only when the deal has a discount" },
  { slot: "{{DiscountText}}", example: "Includes a 10% discount on the licence until 30 April 2027. …" },
  { slot: "{{#FreePeriod}} … {{/FreePeriod}}", example: "Only when the deal starts with a free period" },
  { slot: "{{FreePeriodText}}", example: "Starts with a free pilot until 30 November 2026. …" },
  { slot: "{{#HasServices}} … {{/HasServices}}", example: "Only when the deal has add-ons" },
  { slot: "{{#Services}} … {{/Services}}", example: "One table row per add-on" },
  { slot: "{{Name}} {{Description}} {{Price}}", example: "Onboarding & Setup · … · £450.00" },
  { slot: "{{ServicesTotal}}", example: "£900.00" },
];

/** Fills {first_name}-style slots in the email defaults. Unknown ones are left as typed. */
export function mergeProposalText(text: string, values: Record<string, string>): string {
  return text.replace(/\{(\w+)\}/g, (m, k: string) => values[k] ?? m);
}

export const proposalSettingsInput = z.object({
  validDays: z.coerce.number().int().min(1).max(365),
  stageId: z.string().min(1).nullable(),
  emailSubject: z.string().trim().min(1, "Give the email a subject").max(300),
  emailMessage: z.string().trim().min(1, "Write the message").max(5000),
});

export const proposalCreateInput = z.object({
  contactId: z.string().min(1).nullish().transform((v) => v ?? null),
});

export const proposalSendInput = z.object({
  to: z.string().trim().toLowerCase().email("Enter an email address"),
  subject: z.string().trim().min(1, "Give the email a subject").max(300),
  message: z.string().trim().min(1, "Write a message").max(5000),
});

/** A Word file, base64 in JSON like every other request. */
export const templateUploadInput = z.object({
  fileName: z.string().trim().min(1).max(200),
  content: z.string().min(1).max(20 * 1024 * 1024),
});

export interface ProposalSummary {
  id: string;
  ref: string;
  dealId: string;
  contactId: string | null;
  contactName: string | null;
  createdAt: string;
  createdById: string | null;
  sentAt: string | null;
  sentTo: string | null;
  sentById: string | null;
  /** False once an erasure removed it. */
  hasPdf: boolean;
}

/** What the proposal dialog shows before making one. */
export interface ProposalPreview {
  template: { fileName: string; uploadedAt: string } | null;
  pdfMode: "demo" | "live" | "off";
  /** Whether the hub can email it (Otii's own sending is set up). */
  canEmail: boolean;
  /** The stage the deal would move to, or why it won't. */
  move: { stageId: string; blocked: string | null } | null;
  contacts: { contactId: string; name: string; firstName: string; email: string | null; isKeyPerson: boolean; blocked: boolean }[];
  fields: Record<string, unknown>;
}

export interface ProposalTemplateInfo {
  fileName: string;
  uploadedAt: string;
  uploadedById: string | null;
}

/** A file for the browser to save. */
export interface FileDownload {
  fileName: string;
  contentType: string;
  base64: string;
}

/** Files travel as base64 in JSON, in both directions. */
export function toBase64(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

export function fromBase64(b64: string): Uint8Array {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}
